import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Zap, Crown, Check, Star, Shield, Percent, Gift, Clock,
  ChevronRight, CreditCard, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useConfirm } from '../components/CustomDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeasonPass = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialog } = useConfirm();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    // Check for session_id in URL (returning from Stripe)
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      verifySubscription(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', '/season-pass');
    } else {
      fetchSubscription();
    }
  }, []);

  const verifySubscription = async (sessionId) => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API}/subscription/verify/${sessionId}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`Season Pass activated! You're now a ${response.data.plan} member.`);
        await refreshUser();
      } else {
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify subscription');
    } finally {
      setVerifying(false);
      fetchSubscription();
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscription/create`,
        { 
          plan: plan,
          origin_url: window.location.origin 
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await showConfirm(
      'Are you sure you want to cancel your Season Pass?',
      'Cancel Subscription'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled');
      await refreshUser();
      await fetchSubscription();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Pass',
      price: 9.99,
      period: 'month',
      savings: null,
      features: [
        '5% platform fee (vs 8%)',
        'Priority matchmaking',
        'Exclusive badge',
        'Early access to new games'
      ]
    },
    {
      id: 'annual',
      name: 'Annual Pass',
      price: 79.99,
      period: 'year',
      savings: '33% savings',
      popular: true,
      features: [
        '5% platform fee (vs 8%)',
        'Priority matchmaking',
        'Exclusive gold badge',
        'Early access to new games',
        '$20 bonus credits',
        'VIP support'
      ]
    }
  ];

  const isSubscribed = subscription?.is_active;

  if (verifying) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto text-center py-20">
          <Loader2 className="w-12 h-12 text-[#FFD700] animate-spin mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Verifying Your Subscription</h2>
          <p className="text-white/50">Please wait while we confirm your payment...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="season-pass-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 mb-4">
            <Crown className="w-4 h-4 text-[#FFD700]" />
            <span className="text-sm text-[#FFD700] font-medium">Premium Membership</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">Season Pass</h1>
          <p className="text-white/50 max-w-lg mx-auto">
            Unlock exclusive benefits and save on every match with our Season Pass subscription
          </p>
        </div>

        {/* Current Status */}
        {isSubscribed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-6 mb-8 border-2 border-[#FFD700]/30"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-[#FFD700]" />
                </div>
                <div>
                  <p className="font-heading font-bold text-lg text-[#FFD700]">Season Pass Active</p>
                  <p className="text-white/50 text-sm">
                    {subscription?.plan === 'annual' ? 'Annual' : 'Monthly'} plan • 
                    Renews {new Date(subscription?.current_period_end).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleCancel}
                className="text-white/50 border-white/20"
              >
                Cancel
              </Button>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-[#00FF94]/10 flex items-center gap-2">
              <Check className="w-4 h-4 text-[#00FF94]" />
              <span className="text-sm text-[#00FF94]">
                You're saving 3% on every match fee!
              </span>
            </div>
          </motion.div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`glass-card rounded-2xl p-6 relative ${
                plan.popular 
                  ? 'border-2 border-[#CCFF00] bg-gradient-to-br from-[#CCFF00]/10 to-transparent' 
                  : 'border-2 border-[#00F0FF]/30 bg-gradient-to-br from-[#00F0FF]/5 to-transparent'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#CCFF00] text-black text-xs font-bold">
                  MOST POPULAR
                </div>
              )}
              
              {!plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#00F0FF] text-black text-xs font-bold">
                  STARTER
                </div>
              )}
              
              {plan.savings && (
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#00FF94]/20 text-[#00FF94] text-xs font-medium">
                  {plan.savings}
                </div>
              )}

              <div className="mb-6 mt-2">
                <h3 className={`font-heading font-bold text-xl mb-2 ${!plan.popular ? 'text-[#00F0FF]' : 'text-[#CCFF00]'}`}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className={`font-mono text-4xl font-bold ${!plan.popular ? 'text-[#00F0FF]' : 'text-[#CCFF00]'}`}>
                    ${plan.price}
                  </span>
                  <span className="text-white/50">/{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-[#00FF94] flex-shrink-0" />
                    <span className="text-white/70 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading || isSubscribed}
                className={`w-full py-6 ${
                  plan.popular 
                    ? 'btn-primary clip-slant' 
                    : 'bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold'
                }`}
                data-testid={`subscribe-${plan.id}`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : isSubscribed ? (
                  'Already Subscribed'
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe Now
                  </>
                )}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Benefits Breakdown */}
        <div className="glass-card rounded-2xl p-8">
          <h2 className="font-heading font-bold text-xl mb-6 text-center">Why Season Pass?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#CCFF00]/20 flex items-center justify-center mb-3">
                <Percent className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <h3 className="font-semibold mb-2">Save on Fees</h3>
              <p className="text-white/50 text-sm">
                Pay only 5% platform fee instead of 8% on every completed match
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#00F0FF]/20 flex items-center justify-center mb-3">
                <Zap className="w-6 h-6 text-[#00F0FF]" />
              </div>
              <h3 className="font-semibold mb-2">Priority Matching</h3>
              <p className="text-white/50 text-sm">
                Get matched faster with Quick Match priority queue
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#7000FF]/20 flex items-center justify-center mb-3">
                <Star className="w-6 h-6 text-[#7000FF]" />
              </div>
              <h3 className="font-semibold mb-2">Exclusive Badge</h3>
              <p className="text-white/50 text-sm">
                Stand out with a premium badge on your profile
              </p>
            </div>
          </div>

          <div className="mt-8 p-4 rounded-xl bg-white/5 text-center">
            <p className="text-white/50 text-sm">
              <strong className="text-white">Example:</strong> Win 10 matches at $50 entry fee = 
              <span className="text-[#FF2E2E] mx-1">$80 in fees (8%)</span> vs 
              <span className="text-[#00FF94] mx-1">$50 in fees (5%)</span>
              = <span className="text-[#CCFF00] font-bold">$30 saved!</span>
            </p>
          </div>
        </div>
      </div>
      <ConfirmDialog />
    </DashboardLayout>
  );
};

export default SeasonPass;
